import { WaypointType } from '@treadinc/horizon-api-spec';

import { withDefaultSiteRing } from '~components/Maps/v2/defaultSiteMarkerStyle';

// SVGs are built as strings (not React components) because Google Maps markers
// require a URL. These are encoded as data:image/svg+xml URIs.

const PICKUP_ARROW =
  'M10 18L11.41 19.41L17 13.83V26H19V13.83L24.58 19.42L26 18L18 10L10 18Z';
const DROPOFF_ARROW =
  'M24 16L22.59 14.59L17 20.17V8H15V20.17L9.42 14.58L8 16L16 24L24 16Z';
const BOTH_ARROWS =
  'M20 21.01V14H18V21.01H15L19 25L23 21.01H20ZM13 7L9 10.99H12V18H14V10.99H17L13 7Z';
const STAGING_ARROW =
  'M16 8L14.59 9.41L20.17 15H8V17H20.17L14.58 22.58L16 24L24 16L16 8Z';

const DEFAULT_COLORS: Record<WaypointType | 'both', string> = {
  [WaypointType.PICKUP]: '#2221BB',
  [WaypointType.DROP_OFF]: '#172559',
  [WaypointType.WEIGH_POINT]: '#607D8B',
  [WaypointType.STAGING]: '#0891B2',
  both: '#1B2480',
};

function buildSvg(
  fillColor: string,
  iconPath: string,
  viewBox: string,
  cx: string,
  cy: string,
  r: string,
  letter?: string,
): string {
  const letterEl = letter
    ? `<text x="${cx}" y="29" text-anchor="middle" font-size="11" font-weight="bold" font-family="Arial,sans-serif" fill="white">${letter}</text>`
    : '';

  return (
    `<svg width="24" height="24" viewBox="${viewBox}" fill="none" xmlns="http://www.w3.org/2000/svg">` +
    `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fillColor}" stroke="white" stroke-width="2"/>` +
    `<path d="${iconPath}" fill="white"/>` +
    letterEl +
    '</svg>'
  );
}

function getSvgForType(
  waypointType: WaypointType | 'both',
  color: string,
  letter?: string,
): string {
  switch (waypointType) {
    case WaypointType.PICKUP:
      return buildSvg(color, PICKUP_ARROW, '0 0 36 36', '18', '18', '17', letter);
    case WaypointType.DROP_OFF:
      return buildSvg(color, DROPOFF_ARROW, '0 0 32 32', '16', '16', '15', letter);
    case WaypointType.WEIGH_POINT:
      return buildSvg(
        color,
        '',
        '0 0 32 32',
        '16',
        '16',
        '15',
        letter ? `W${letter}` : 'W',
      );
    case WaypointType.STAGING:
      return buildSvg(color, STAGING_ARROW, '0 0 32 32', '16', '16', '15', letter);
    case 'both':
      return buildSvg(color, BOTH_ARROWS, '0 0 32 32', '16', '16', '15', letter);
  }
}

const toDataUrl = (svg: string): string =>
  'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);

/**
 * Create an SVG data URL for a waypoint marker using the default color for its
 * type. Pass `isDefaultSite` to render the muted + same-color dashed-ring
 * (candidate-site) treatment.
 */
export function createWaypointSvgDataUrl(
  waypointType: WaypointType | 'both',
  isDefaultSite = false,
): string {
  const color = DEFAULT_COLORS[waypointType];
  const svg = getSvgForType(waypointType, color);
  return toDataUrl(isDefaultSite ? withDefaultSiteRing(svg, color) : svg);
}

/**
 * Create an SVG data URL for a waypoint marker with a custom color and letter label.
 * Used by the order map to differentiate jobs.
 */
export function createLabeledWaypointSvgDataUrl(
  waypointType: WaypointType | 'both',
  color: string,
  letter?: string,
  isDefaultSite = false,
): string {
  const svg = getSvgForType(waypointType, color, letter);
  return toDataUrl(isDefaultSite ? withDefaultSiteRing(svg, color) : svg);
}

export const JOB_COLOR_PALETTE = [
  '#2196F3',
  '#4CAF50',
  '#FF9800',
  '#E91E63',
  '#9C27B0',
  '#00BCD4',
  '#FF5722',
  '#607D8B',
  '#795548',
  '#3F51B5',
  '#009688',
  '#CDDC39',
];
